import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, eur, deDate } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Plus, Printer, Eye, Download, Mail, Ban, FileBarChart } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function Verkauf() {
  const [sales, setSales] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/sales").then((r) => setSales(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    const { data } = await api.get(`/sales/${id}`);
    setDetail(data);
  };

  const openInvoicePdf = async (invoiceId, download = false) => {
    const { data } = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: "blob" });
    const blob = new Blob([data], { type: "application/pdf" });
    if (download) {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${detail.invoice.number}.pdf`;
      a.click();
    } else {
      window.open(URL.createObjectURL(blob), "_blank");
    }
  };

  const resendInvoice = async (invoiceId) => {
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/send-email`);
      toast.success(`Rechnung gesendet an ${data.sent_to}`);
      openDetail(detail.id);
    } catch (e) {
      toast.error(apiError(e, "Versand fehlgeschlagen"));
    }
  };

  const stornoInvoice = async (invoiceId) => {
    if (!window.confirm("Rechnung wirklich stornieren? Es wird eine Gutschrift/Storno-Beleg erstellt.")) return;
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/storno`);
      toast.success(`Storniert – Gutschrift ${data.storno_number} erstellt.`);
      openDetail(detail.id);
      load();
    } catch (e) {
      toast.error(apiError(e, "Storno fehlgeschlagen"));
    }
  };

  return (
    <div className="space-y-6" data-testid="verkauf-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">Verkauf</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Verkäufe</h1>
        </div>
        <Link to="/app/verkauf/neu">
          <Button className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="new-sale-button"><Plus className="w-4 h-4 mr-2" />Neuer Verkauf</Button>
        </Link>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead><tr className="border-b border-zinc-100 text-left">
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Nummer</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Datum</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Kunde</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Artikel</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">Einkauf</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">Verkauf</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">Steuer</th>
            <th className="px-5 py-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400 text-right">Gewinn n. St.</th>
          </tr></thead>
          <tbody className="divide-y divide-zinc-50">
            {loading && <tr><td colSpan={8} className="px-5 py-8 text-center text-zinc-400 font-mono text-xs">Laden…</td></tr>}
            {!loading && sales.length === 0 && <tr><td colSpan={8} className="px-5 py-8 text-center text-zinc-400">Noch keine Verkäufe.</td></tr>}
            {sales.map((s) => (
              <tr key={s.id} onClick={() => openDetail(s.id)} data-testid={`sale-row-${s.number}`} className="hover:bg-zinc-50 cursor-pointer">
                <td className="px-5 py-3 font-mono text-xs">{s.number}{s.status === "storniert" && <span className="ml-2 text-[9px] text-red-600 border border-red-200 bg-red-50 px-1.5 py-0.5 rounded-full">STORNIERT</span>}</td>
                <td className="px-5 py-3 text-zinc-600">{deDate(s.date)}</td>
                <td className="px-5 py-3">{s.customer_name}</td>
                <td className="px-5 py-3 text-zinc-500">{s.items?.map((i) => i.name).join(", ")}</td>
                <td className="px-5 py-3 text-right text-zinc-500">{eur(s.purchase_cost)}</td>
                <td className="px-5 py-3 text-right font-medium">{eur(s.total)}</td>
                <td className="px-5 py-3 text-xs text-zinc-500">{s.tax_label || s.tax_mode}</td>
                <td className="px-5 py-3 text-right font-medium text-zinc-900">{eur(s.profit_after_tax ?? s.profit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="sm:max-w-xl" data-testid="sale-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-mono text-base">Verkauf {detail.number}</DialogTitle></DialogHeader>
              <div className="text-sm space-y-4">
                <div className="grid grid-cols-2 gap-3 text-zinc-600">
                  <div>Kunde: {detail.customer_name}</div>
                  <div>Kanal: <span className="capitalize">{detail.channel}</span></div>
                  <div>Zahlung: <span className="capitalize">{detail.payment_method}</span> · {detail.payment_status}</div>
                  <div>Steuerart: {detail.tax_label || detail.tax_mode}</div>
                  <div>Steuerbetrag: {eur(detail.tax)}</div>
                </div>
                <div className="border border-zinc-100 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-zinc-50 text-left"><th className="px-3 py-2 text-xs font-mono text-zinc-400">Artikel</th><th className="px-3 py-2 text-xs font-mono text-zinc-400">Seriennr.</th><th className="px-3 py-2 text-xs font-mono text-zinc-400 text-right">Preis</th></tr></thead>
                    <tbody>
                      {detail.items?.map((i, x) => (
                        <tr key={x} className="border-t border-zinc-50"><td className="px-3 py-2">{i.name}</td><td className="px-3 py-2 font-mono text-xs text-zinc-500">{i.serial || "–"}</td><td className="px-3 py-2 text-right">{eur(i.price)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="border border-zinc-100 rounded-lg p-3"><div className="text-[9px] font-mono uppercase text-zinc-400">Umsatz</div><div className="font-semibold">{eur(detail.total)}</div></div>
                  <div className="border border-zinc-100 rounded-lg p-3"><div className="text-[9px] font-mono uppercase text-zinc-400">EK-Kosten</div><div className="font-semibold">{eur(detail.purchase_cost)}</div></div>
                  <div className="bg-zinc-900 text-white rounded-lg p-3"><div className="text-[9px] font-mono uppercase text-zinc-400">Gewinn nach Steuer</div><div className="font-semibold">{eur(detail.profit_after_tax ?? detail.profit)} ({detail.margin} %)</div></div>
                </div>
                {detail.invoice && (
                  <div className="space-y-3">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Kundenrechnung {detail.invoice.number} {detail.invoice.status === "storniert" && <span className="text-red-600">· STORNIERT</span>} {detail.invoice.email_sent_at && <span className="text-emerald-600">· per E-Mail gesendet</span>}</div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button variant="outline" onClick={() => openInvoicePdf(detail.invoice.id)} data-testid="sale-invoice-view" className="min-h-[44px]"><Eye className="w-4 h-4 mr-1" /> Ansehen</Button>
                      <Button variant="outline" onClick={() => openInvoicePdf(detail.invoice.id)} data-testid="sale-invoice-print" className="min-h-[44px]"><Printer className="w-4 h-4 mr-1" /> Drucken</Button>
                      <Button variant="outline" onClick={() => openInvoicePdf(detail.invoice.id, true)} data-testid="sale-invoice-download" className="min-h-[44px]"><Download className="w-4 h-4 mr-1" /> PDF speichern</Button>
                      <Button variant="outline" onClick={() => resendInvoice(detail.invoice.id)} data-testid="sale-invoice-resend" className="min-h-[44px]"><Mail className="w-4 h-4 mr-1" /> Erneut senden</Button>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/app/verkauf/${detail.id}/beleg`} className="flex-1"><Button variant="outline" data-testid="sale-profit-pdf" className="w-full min-h-[44px] border-zinc-900"><FileBarChart className="w-4 h-4 mr-1" /> Buchhaltungsbeleg (INTERN)</Button></Link>
                      {detail.invoice.status !== "storniert" && (
                        <Button variant="outline" onClick={() => stornoInvoice(detail.invoice.id)} data-testid="sale-invoice-storno" className="min-h-[44px] text-red-600 border-red-200 hover:bg-red-50"><Ban className="w-4 h-4 mr-1" /> Storno</Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
